import { cva, type VariantProps } from 'class-variance-authority';
import { cn, focusRing } from '@/lib/utils';

/* Single source of variant truth for every button-like surface
 * (Button, ButtonLink, ButtonAnchor). Keeps internal/external links
 * visually identical while letting each component pick the right
 * underlying element for semantics + a11y.
 */
export const buttonStyles = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium leading-none',
    'transition-[transform,box-shadow,background-color,color,opacity] duration-200',
    'touch-manipulation',
    'disabled:pointer-events-none disabled:opacity-50',
    focusRing,
  ),
  {
    variants: {
      variant: {
        // Gradient cyan-to-teal pill — primary CTAs
        primary: cn(
          'rounded-full bg-gradient-brand text-primary-foreground shadow-glow-primary',
          'hover:opacity-90 hover:-translate-y-0.5',
          'active:translate-y-0 active:scale-[0.98]',
        ),
        // Glassmorphic pill — secondary CTAs
        secondary: cn(
          'rounded-full border border-outline-variant bg-white/60 backdrop-blur-xl text-primary',
          'hover:bg-surface-container',
        ),
        // Solid black pill — used on dark navy panels (e.g. contact submit)
        outline: cn(
          'rounded-full border border-outline-variant bg-surface text-primary',
          'hover:bg-surface-container',
        ),
        ghost:
          'rounded-md bg-transparent text-fg-muted hover:bg-surface-container hover:text-primary',
        link: 'rounded-md bg-transparent text-tertiary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-5 text-sm',
        md: 'h-11 px-7 text-sm',
        lg: 'h-12 px-8 text-base',
        icon: 'size-10 rounded-full p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonStyles>;
export type ButtonStyleVariant = NonNullable<ButtonVariantProps['variant']>;
export type ButtonStyleSize = NonNullable<ButtonVariantProps['size']>;
